/**
 * Date Formatting Utilities
 *
 * Provides timezone and locale-aware date formatting across the application.
 * User preferences are respected for all date displays.
 */

// Common IANA timezone list for dropdown
export const TIMEZONES = [
  // UTC
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)', offset: '+00:00' },

  // Americas
  { value: 'America/New_York', label: 'Eastern Time (US & Canada)', offset: '-05:00' },
  { value: 'America/Chicago', label: 'Central Time (US & Canada)', offset: '-06:00' },
  { value: 'America/Denver', label: 'Mountain Time (US & Canada)', offset: '-07:00' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US & Canada)', offset: '-08:00' },
  { value: 'America/Anchorage', label: 'Alaska', offset: '-09:00' },
  { value: 'Pacific/Honolulu', label: 'Hawaii', offset: '-10:00' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)', offset: '-07:00' },
  { value: 'America/Toronto', label: 'Toronto', offset: '-05:00' },
  { value: 'America/Vancouver', label: 'Vancouver', offset: '-08:00' },
  { value: 'America/Mexico_City', label: 'Mexico City', offset: '-06:00' },
  { value: 'America/Bogota', label: 'Bogota', offset: '-05:00' },
  { value: 'America/Lima', label: 'Lima', offset: '-05:00' },
  { value: 'America/Buenos_Aires', label: 'Buenos Aires', offset: '-03:00' },
  { value: 'America/Sao_Paulo', label: 'Sao Paulo', offset: '-03:00' },

  // Europe
  { value: 'Europe/London', label: 'London', offset: '+00:00' },
  { value: 'Europe/Paris', label: 'Paris', offset: '+01:00' },
  { value: 'Europe/Berlin', label: 'Berlin', offset: '+01:00' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam', offset: '+01:00' },
  { value: 'Europe/Brussels', label: 'Brussels', offset: '+01:00' },
  { value: 'Europe/Madrid', label: 'Madrid', offset: '+01:00' },
  { value: 'Europe/Rome', label: 'Rome', offset: '+01:00' },
  { value: 'Europe/Zurich', label: 'Zurich', offset: '+01:00' },
  { value: 'Europe/Vienna', label: 'Vienna', offset: '+01:00' },
  { value: 'Europe/Warsaw', label: 'Warsaw', offset: '+01:00' },
  { value: 'Europe/Stockholm', label: 'Stockholm', offset: '+01:00' },
  { value: 'Europe/Helsinki', label: 'Helsinki', offset: '+02:00' },
  { value: 'Europe/Athens', label: 'Athens', offset: '+02:00' },
  { value: 'Europe/Istanbul', label: 'Istanbul', offset: '+03:00' },
  { value: 'Europe/Moscow', label: 'Moscow', offset: '+03:00' },

  // Asia
  { value: 'Asia/Dubai', label: 'Dubai', offset: '+04:00' },
  { value: 'Asia/Karachi', label: 'Karachi', offset: '+05:00' },
  { value: 'Asia/Kolkata', label: 'Mumbai, Kolkata, New Delhi', offset: '+05:30' },
  { value: 'Asia/Dhaka', label: 'Dhaka', offset: '+06:00' },
  { value: 'Asia/Bangkok', label: 'Bangkok', offset: '+07:00' },
  { value: 'Asia/Singapore', label: 'Singapore', offset: '+08:00' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong', offset: '+08:00' },
  { value: 'Asia/Shanghai', label: 'Beijing, Shanghai', offset: '+08:00' },
  { value: 'Asia/Taipei', label: 'Taipei', offset: '+08:00' },
  { value: 'Asia/Seoul', label: 'Seoul', offset: '+09:00' },
  { value: 'Asia/Tokyo', label: 'Tokyo', offset: '+09:00' },

  // Australia & Pacific
  { value: 'Australia/Perth', label: 'Perth', offset: '+08:00' },
  { value: 'Australia/Adelaide', label: 'Adelaide', offset: '+09:30' },
  { value: 'Australia/Darwin', label: 'Darwin', offset: '+09:30' },
  { value: 'Australia/Brisbane', label: 'Brisbane', offset: '+10:00' },
  { value: 'Australia/Sydney', label: 'Sydney', offset: '+10:00' },
  { value: 'Australia/Melbourne', label: 'Melbourne', offset: '+10:00' },
  { value: 'Pacific/Auckland', label: 'Auckland', offset: '+12:00' },
  { value: 'Pacific/Fiji', label: 'Fiji', offset: '+12:00' },

  // Africa & Middle East
  { value: 'Africa/Cairo', label: 'Cairo', offset: '+02:00' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg', offset: '+02:00' },
  { value: 'Africa/Lagos', label: 'Lagos', offset: '+01:00' },
  { value: 'Africa/Nairobi', label: 'Nairobi', offset: '+03:00' },
  { value: 'Asia/Jerusalem', label: 'Jerusalem', offset: '+02:00' },
  { value: 'Asia/Riyadh', label: 'Riyadh', offset: '+03:00' },
]

// Common locale list for dropdown
export const LOCALES = [
  { value: 'en-US', label: 'English (United States)', dateFormat: 'MM/DD/YYYY' },
  { value: 'en-GB', label: 'English (United Kingdom)', dateFormat: 'DD/MM/YYYY' },
  { value: 'en-AU', label: 'English (Australia)', dateFormat: 'DD/MM/YYYY' },
  { value: 'en-CA', label: 'English (Canada)', dateFormat: 'YYYY-MM-DD' },
  { value: 'en-IN', label: 'English (India)', dateFormat: 'DD-MM-YYYY' },
  { value: 'de-DE', label: 'Deutsch (Deutschland)', dateFormat: 'DD.MM.YYYY' },
  { value: 'de-AT', label: 'Deutsch (Osterreich)', dateFormat: 'DD.MM.YYYY' },
  { value: 'de-CH', label: 'Deutsch (Schweiz)', dateFormat: 'DD.MM.YYYY' },
  { value: 'fr-FR', label: 'Francais (France)', dateFormat: 'DD/MM/YYYY' },
  { value: 'fr-CA', label: 'Francais (Canada)', dateFormat: 'YYYY-MM-DD' },
  { value: 'fr-CH', label: 'Francais (Suisse)', dateFormat: 'DD.MM.YYYY' },
  { value: 'es-ES', label: 'Espanol (Espana)', dateFormat: 'DD/MM/YYYY' },
  { value: 'es-MX', label: 'Espanol (Mexico)', dateFormat: 'DD/MM/YYYY' },
  { value: 'es-AR', label: 'Espanol (Argentina)', dateFormat: 'DD/MM/YYYY' },
  { value: 'it-IT', label: 'Italiano (Italia)', dateFormat: 'DD/MM/YYYY' },
  { value: 'pt-BR', label: 'Portugues (Brasil)', dateFormat: 'DD/MM/YYYY' },
  { value: 'pt-PT', label: 'Portugues (Portugal)', dateFormat: 'DD/MM/YYYY' },
  { value: 'nl-NL', label: 'Nederlands (Nederland)', dateFormat: 'DD-MM-YYYY' },
  { value: 'nl-BE', label: 'Nederlands (Belgie)', dateFormat: 'DD/MM/YYYY' },
  { value: 'pl-PL', label: 'Polski (Polska)', dateFormat: 'DD.MM.YYYY' },
  { value: 'ru-RU', label: 'Russkij (Rossiya)', dateFormat: 'DD.MM.YYYY' },
  { value: 'ja-JP', label: 'Nihongo (Nihon)', dateFormat: 'YYYY/MM/DD' },
  { value: 'zh-CN', label: 'Zhongwen (Zhongguo)', dateFormat: 'YYYY/MM/DD' },
  { value: 'zh-TW', label: 'Zhongwen (Taiwan)', dateFormat: 'YYYY/MM/DD' },
  { value: 'ko-KR', label: 'Hangugeo (Hanguk)', dateFormat: 'YYYY.MM.DD' },
  { value: 'ar-SA', label: 'Arabic (Saudi Arabia)', dateFormat: 'DD/MM/YYYY' },
  { value: 'he-IL', label: 'Hebrew (Israel)', dateFormat: 'DD/MM/YYYY' },
  { value: 'hi-IN', label: 'Hindi (Bharat)', dateFormat: 'DD-MM-YYYY' },
  { value: 'th-TH', label: 'Thai (Thailand)', dateFormat: 'DD/MM/YYYY' },
  { value: 'vi-VN', label: 'Vietnamese (Vietnam)', dateFormat: 'DD/MM/YYYY' },
  { value: 'sv-SE', label: 'Svenska (Sverige)', dateFormat: 'YYYY-MM-DD' },
  { value: 'da-DK', label: 'Dansk (Danmark)', dateFormat: 'DD-MM-YYYY' },
  { value: 'nb-NO', label: 'Norsk (Norge)', dateFormat: 'DD.MM.YYYY' },
  { value: 'fi-FI', label: 'Suomi (Suomi)', dateFormat: 'DD.MM.YYYY' },
]

export interface UserDatePreferences {
  timezone?: string
  locale?: string
}

/**
 * Get the current timezone offset string for a timezone
 */
export function getTimezoneOffset(timezone: string): string {
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    })
    const parts = formatter.formatToParts(now)
    const offsetPart = parts.find(p => p.type === 'timeZoneName')
    return offsetPart?.value || ''
  } catch {
    return ''
  }
}

/**
 * Format a timestamp as a localized date string
 * @param timestamp - Unix timestamp in milliseconds, or Date object
 * @param preferences - User's timezone and locale preferences
 * @param options - Additional Intl.DateTimeFormat options
 */
export function formatDate(
  timestamp: number | Date | null | undefined,
  preferences?: UserDatePreferences,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!timestamp) return '-'

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
  if (isNaN(date.getTime())) return '-'

  const timezone = preferences?.timezone || 'UTC'
  const locale = preferences?.locale || 'en-US'

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: timezone,
    ...options,
  }

  try {
    return date.toLocaleDateString(locale, defaultOptions)
  } catch {
    // Fallback if timezone is invalid
    return date.toLocaleDateString('en-US', { ...defaultOptions, timeZone: 'UTC' })
  }
}

/**
 * Format a timestamp as a localized date and time string
 * @param timestamp - Unix timestamp in milliseconds, or Date object
 * @param preferences - User's timezone and locale preferences
 * @param options - Additional Intl.DateTimeFormat options
 */
export function formatDateTime(
  timestamp: number | Date | null | undefined,
  preferences?: UserDatePreferences,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!timestamp) return '-'

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
  if (isNaN(date.getTime())) return '-'

  const timezone = preferences?.timezone || 'UTC'
  const locale = preferences?.locale || 'en-US'

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
    ...options,
  }

  try {
    return date.toLocaleString(locale, defaultOptions)
  } catch {
    // Fallback if timezone is invalid
    return date.toLocaleString('en-US', { ...defaultOptions, timeZone: 'UTC' })
  }
}

/**
 * Format a timestamp as a localized time string
 * @param timestamp - Unix timestamp in milliseconds, or Date object
 * @param preferences - User's timezone and locale preferences
 * @param options - Additional Intl.DateTimeFormat options
 */
export function formatTime(
  timestamp: number | Date | null | undefined,
  preferences?: UserDatePreferences,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!timestamp) return '-'

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
  if (isNaN(date.getTime())) return '-'

  const timezone = preferences?.timezone || 'UTC'
  const locale = preferences?.locale || 'en-US'

  const defaultOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
    ...options,
  }

  try {
    return date.toLocaleTimeString(locale, defaultOptions)
  } catch {
    // Fallback if timezone is invalid
    return date.toLocaleTimeString('en-US', { ...defaultOptions, timeZone: 'UTC' })
  }
}

/**
 * Format a timestamp as a relative time string (e.g., "2 hours ago", "in 3 days")
 * @param timestamp - Unix timestamp in milliseconds, or Date object
 * @param preferences - User's locale preference (timezone not applicable for relative time)
 */
export function formatRelativeTime(
  timestamp: number | Date | null | undefined,
  preferences?: UserDatePreferences
): string {
  if (!timestamp) return '-'

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp)
  if (isNaN(date.getTime())) return '-'

  const locale = preferences?.locale || 'en-US'
  const now = Date.now()
  const diff = date.getTime() - now
  const absDiff = Math.abs(diff)

  // Define time units in milliseconds
  const minute = 60 * 1000
  const hour = 60 * minute
  const day = 24 * hour
  const week = 7 * day
  const month = 30 * day
  const year = 365 * day

  let value: number
  let unit: Intl.RelativeTimeFormatUnit

  if (absDiff < minute) {
    return diff < 0 ? 'Just now' : 'In a moment'
  } else if (absDiff < hour) {
    value = Math.round(diff / minute)
    unit = 'minute'
  } else if (absDiff < day) {
    value = Math.round(diff / hour)
    unit = 'hour'
  } else if (absDiff < week) {
    value = Math.round(diff / day)
    unit = 'day'
  } else if (absDiff < month) {
    value = Math.round(diff / week)
    unit = 'week'
  } else if (absDiff < year) {
    value = Math.round(diff / month)
    unit = 'month'
  } else {
    value = Math.round(diff / year)
    unit = 'year'
  }

  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
    return rtf.format(value, unit)
  } catch {
    // Fallback for unsupported locales
    const absValue = Math.abs(value)
    const unitStr = absValue === 1 ? unit : `${unit}s`
    return diff < 0 ? `${absValue} ${unitStr} ago` : `in ${absValue} ${unitStr}`
  }
}

/**
 * Format a date for display in charts (short format)
 * @param timestamp - Unix timestamp in milliseconds, or Date object
 * @param preferences - User's timezone and locale preferences
 */
export function formatChartDate(
  timestamp: number | Date | null | undefined,
  preferences?: UserDatePreferences
): string {
  return formatDate(timestamp, preferences, {
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format a date for display in long format (e.g., "January 2, 2024")
 * @param timestamp - Unix timestamp in milliseconds, or Date object
 * @param preferences - User's timezone and locale preferences
 */
export function formatLongDate(
  timestamp: number | Date | null | undefined,
  preferences?: UserDatePreferences
): string {
  return formatDate(timestamp, preferences, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Format a date range
 * @param start - Start timestamp
 * @param end - End timestamp
 * @param preferences - User's timezone and locale preferences
 */
export function formatDateRange(
  start: number | Date | null | undefined,
  end: number | Date | null | undefined,
  preferences?: UserDatePreferences
): string {
  const startStr = formatDate(start, preferences, { month: 'short', day: 'numeric' })
  const endStr = formatDate(end, preferences, { month: 'short', day: 'numeric', year: 'numeric' })
  return `${startStr} - ${endStr}`
}

/**
 * Get the browser's detected timezone
 */
export function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'UTC'
  }
}

/**
 * Get the browser's detected locale
 */
export function getBrowserLocale(): string {
  try {
    return navigator.language || 'en-US'
  } catch {
    return 'en-US'
  }
}

/**
 * Check if a timezone string is valid
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone })
    return true
  } catch {
    return false
  }
}

/**
 * Check if a locale string is valid
 */
export function isValidLocale(locale: string): boolean {
  try {
    new Intl.DateTimeFormat(locale)
    return true
  } catch {
    return false
  }
}
